/**
 * Data Agent Types
 *
 * Types for data agents that execute ELT jobs.
 */

/**
 * Data agent status (computed from heartbeat)
 */
export type DataAgentStatus = 'online' | 'offline' | 'unknown';

/**
 * Data agent operational configuration
 * All fields are optional - defaults are applied server-side
 */
export interface DataAgentConfig {
  /** Heartbeat interval in milliseconds (30,000-300,000, default: 30,000) */
  heartbeatIntervalMs?: number;
  /** Poll interval when idle in milliseconds (60,000-600,000, default: 60,000) */
  pollIntervalMs?: number;
  /** Poll interval when busy in milliseconds (5,000-60,000, default: 5,000) */
  pollIntervalBusyMs?: number;
  /** Maximum concurrent jobs (fixed at 1) */
  maxConcurrentJobs?: number;
  /** Query timeout in milliseconds (60,000-86,400,000, default: 3,600,000) */
  queryTimeoutMs?: number;
  /** Maximum rows per query (10,000-1,000,000, default: 1,000,000) */
  maxRowsPerQuery?: number;
}

/**
 * Default values for data agent config (matches backend defaults)
 */
export const DATA_AGENT_CONFIG_DEFAULTS: Required<DataAgentConfig> = {
  heartbeatIntervalMs: 30000,
  pollIntervalMs: 60000,
  pollIntervalBusyMs: 5000,
  maxConcurrentJobs: 1,
  queryTimeoutMs: 3600000,
  maxRowsPerQuery: 1000000,
};

/**
 * Data Agent entity
 */
export interface DataAgent {
  tenantId: string;
  dataAgentId: string;
  dataAgentCode: string;
  dataAgentName: string;
  description?: string;
  isActive: boolean;

  // Operational configuration
  config?: DataAgentConfig;

  // Token info (hash not exposed to frontend)
  tokenExpiresAt?: number; // Unix timestamp
  tokenGeneratedAt?: string;
  tokenRevokedAt?: string;

  // Status tracking
  lastHeartbeatAt?: string;
  status: DataAgentStatus; // Computed by backend from lastHeartbeatAt

  // Audit
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * DTO for data agent creation
 */
export interface DataAgentCreateDTO {
  dataAgentCode: string;
  dataAgentName: string;
  description?: string;
  config?: DataAgentConfig;
}

/**
 * DTO for data agent updates
 */
export interface DataAgentUpdateDTO {
  dataAgentName?: string;
  description?: string;
  isActive?: boolean;
  config?: DataAgentConfig;
}

/**
 * DTO for generating a new agent token
 */
export interface DataAgentGenerateTokenDTO {
  expiresInDays?: number; // Default 180, max 365
}

/**
 * Response for data agent token generation (includes raw token)
 */
export interface DataAgentTokenResponse {
  dataAgentId: string;
  dataAgentCode: string;
  token: string; // Raw token (shown ONLY on generation)
  expiresAt: string; // ISO 8601
  generatedAt: string;
}

/**
 * Query parameters for listing data agents
 */
export interface DataAgentListQuery {
  search?: string;
  isActive?: boolean;
  status?: DataAgentStatus;
  limit?: number;
  nextToken?: string;
}

/**
 * Response for listing data agents
 */
export interface DataAgentListResponse {
  dataAgents: DataAgent[];
  nextToken?: string;
  total?: number;
}
